// batch-70 vakantiecampagne (FO-525 §C10-§C12) - het advies TVT/ATV en de
// doorrekening 2027, puur en getest.
//
// Drie stappen, elk een pure functie:
//   1. `berekenOpnamegraden` - hoeveel van het beschikbare verlof per potje werd
//      gebruikt in het jaar (uit het jaaroverzicht van het ledger).
//   2. `bepaalOpbouwAdvies` - de adviesregels van §C11 met de drempels uit het
//      verlofbeleid. De redenen zijn codes met de echte getallen; de zinnen staan in
//      een vaste tekstset (`ADVIES_REDEN_TEKSTEN` in @streep/types), nooit vrij
//      gegenereerd.
//   3. `berekenDoorrekening2027` - per scenario (houden, advies, zelf) wat er in het
//      nieuwe jaar bijkomt, wat er wordt uitbetaald en het verwachte saldo eind van
//      het jaar. Alleen uren; geen geld.
//   4. `berekenKeuzeKaarten` - diezelfde cijfers per keuze (TVT, ATV, samen), zoals
//      stap 2 ze toont, inclusief de TVT-ingang per 1 december of 1 januari (A9).
//
// Twee bewuste keuzes (CC.0b, STATUS-vakantiecampagne):
//
// - DE OPNAMEGRAAD REKENT OP BEHOEFTE, NIET OP DE AFBOEKVOLGORDE. Het ledger boekt
//   verlof af in de volgorde van het beleid: sinds batch-69 eerst TVT, dan ATV, dan
//   wettelijk en bovenwettelijk (`STANDAARD_AUTO_AFBOEK_EMMERS`). Per potje gelezen
//   zou "opgenomen wettelijk" daardoor laag uitvallen bij precies de mensen die hun
//   ATV en TVT gebruiken, en het advies ze dan "zet TVT en ATV uit" geven. De graad
//   kijkt daarom naar het totaal dat iemand opnam en wijst dat toe in de volgorde
//   van behoefte: eerst het gewone verlof (wettelijk + bovenwettelijk), dan de
//   gespaarde overuren en reistijd (TVT), dan ATV. Uitbetaalde TVT/ATV telt als
//   gebruikt (§C10). Zo zegt "gewoon 63 %" iets over de persoon, niet over de
//   afboekvolgorde. De zinnen noemen daarnaast de echte ledgerstanden (wat er eind
//   van het jaar per potje over is).
// - ATV UIT = 38 UUR WERKEN (A2, variant b, de default). Zonder ATV is er geen TVT
//   (CAO-regel in `tvtFactor`: geen ATV = geen TVT). Advies "ATV uit" zet de TVT
//   dus feitelijk ook op uitbetalen, met een eigen reden. Variant (a) (40 uur werken,
//   ATV uitbetaald, TVT blijft mogelijk) zit erin als parameter `atvUitVariant`, voor
//   als de PO daartoe besluit (vergt een payroll-uitbreiding, eigen FO).

import type {
  AdviesCategorie,
  AdviesRedenCode,
  AdviesRedenDTO,
  AtvUitVariant,
  CampagneTvtIngang,
  Doorrekening2027DTO,
  SpaarKeuze,
} from '@streep/types'
import { round2, type JaaroverzichtEmmer, type KlassiekeEmmerType } from './leave-calculations'
import { overuurGeldFactor, reisGeldFactor, tvtFactor } from './payroll-calculations'
import type { OpbouwKeuze } from './werktijd-sparen-uitleg'

export type { AdviesCategorie, AdviesRedenCode, AtvUitVariant }

/** A2: (b) = ATV uit betekent 38 uur werken zonder opbouw en zonder uitbetaling (default). */
export const STANDAARD_ATV_UIT_VARIANT: AtvUitVariant = 'B_38_UUR'

/** Minimaal aantal maanden in dienst in het jaar voor een advies (§C10). */
export const ADVIES_MIN_MAANDEN_IN_DIENST = 6

/** De drempels van het advies uit `VerlofBeleid` (A1; defaults 0,75 / 0,50 / 0,50 / 0,25). */
export interface AdviesDrempels {
  gewoon: number
  atv: number
  tvtHalf: number
  tvtUit: number
}

export const STANDAARD_ADVIES_DREMPELS: AdviesDrempels = { gewoon: 0.75, atv: 0.5, tvtHalf: 0.5, tvtUit: 0.25 }

// ────────────────────────────────────────────────────────────────────────────
// 1. Opnamegraden
// ────────────────────────────────────────────────────────────────────────────

/** Het deel van het jaaroverzicht dat de opnamegraad leest. */
export type JaaroverzichtVoorAdvies = Record<
  KlassiekeEmmerType,
  Pick<JaaroverzichtEmmer, 'start' | 'erbij' | 'opgenomen' | 'nogGereserveerd' | 'correcties' | 'uitbetaald' | 'eind'>
>

export interface PotGraad {
  /** Beschikbaar in het jaar: start + erbij + correcties (niet negatief). */
  beschikbaar: number
  /** Gebruikt volgens de behoefte (zie de kop van dit bestand), inclusief uitbetaald. */
  gebruikt: number
  /** gebruikt / beschikbaar; null = niets beschikbaar (geen oordeel mogelijk). */
  graad: number | null
  /** Wat er eind van het jaar volgens het ledger in het potje over is. */
  overEind: number
}

export interface Opnamegraden {
  /** Genoeg maanden in dienst in het jaar voor een advies (§C10). */
  voldoendeInDienst: boolean
  maandenInDienst: number
  /** Totaal opgenomen in het jaar, alle potjes, inclusief het nog geplande verlof. */
  totaalOpgenomen: number
  gewoon: PotGraad
  atv: PotGraad
  tvtWerk: PotGraad
  tvtReis: PotGraad
  /** Opname boven alle potjes samen (het saldo ging in de min). */
  tekort: number
}

/**
 * Hele kalendermaanden in dienst binnen het jaar (afgerond naar beneden). Datums als
 * UTC-middernacht van de NL-dag (zoals `Employee.startDate`); `uitDienst` is de laatste
 * dag in dienst.
 */
export function maandenInDienstInJaar(jaar: number, inDienstSinds: Date | null, uitDienst: Date | null = null): number {
  const dag = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  const van = new Date(Math.max(Date.UTC(jaar, 0, 1), inDienstSinds ? dag(inDienstSinds) : 0))
  // Exclusief einde: de dag na de laatste dag in dienst, uiterlijk 1 januari van het jaar erna.
  const tot = new Date(Math.min(Date.UTC(jaar + 1, 0, 1), uitDienst ? dag(uitDienst) + 86_400_000 : Number.MAX_SAFE_INTEGER))
  if (tot.getTime() <= van.getTime()) return 0
  const maanden =
    (tot.getUTCFullYear() - van.getUTCFullYear()) * 12 +
    (tot.getUTCMonth() - van.getUTCMonth()) -
    (tot.getUTCDate() < van.getUTCDate() ? 1 : 0)
  return Math.max(0, maanden)
}

function potGraad(beschikbaar: number, gebruikt: number, overEind: number): PotGraad {
  const b = round2(Math.max(0, beschikbaar))
  const g = round2(Math.max(0, gebruikt))
  return { beschikbaar: b, gebruikt: g, graad: b > 0 ? round2(g / b) : null, overEind: round2(overEind) }
}

/**
 * De opnamegraden van een jaar (§C10), op behoefte: het totaal opgenomen verlof
 * gaat eerst naar het gewone verlof, dan naar TVT (overuren, dan reistijd), dan naar
 * ATV. Uitbetaalde TVT en ATV tellen als gebruikt.
 */
export function berekenOpnamegraden(input: {
  jaaroverzicht: JaaroverzichtVoorAdvies
  maandenInDienst: number
}): Opnamegraden {
  const j = input.jaaroverzicht
  const beschikbaar = (t: KlassiekeEmmerType) => Math.max(0, j[t].start + j[t].erbij + j[t].correcties)
  const totaalOpgenomen = round2(
    (['WETTELIJK', 'BOVENWETTELIJK', 'ATV', 'TVT_WERK', 'TVT_REIS'] as const).reduce(
      (s, t) => s + j[t].opgenomen + j[t].nogGereserveerd,
      0,
    ),
  )
  const gewoonBeschikbaar = beschikbaar('WETTELIJK') + beschikbaar('BOVENWETTELIJK')
  const gewoonGebruikt = Math.min(Math.max(0, totaalOpgenomen), gewoonBeschikbaar)
  let rest = Math.max(0, totaalOpgenomen - gewoonGebruikt)

  const spaarpot = (t: 'TVT_WERK' | 'TVT_REIS' | 'ATV'): PotGraad => {
    const b = beschikbaar(t)
    const uitbetaald = Math.min(b, Math.max(0, j[t].uitbetaald))
    const verlof = Math.min(rest, Math.max(0, b - uitbetaald))
    rest = Math.max(0, rest - verlof)
    return potGraad(b, uitbetaald + verlof, j[t].eind)
  }
  const tvtWerk = spaarpot('TVT_WERK')
  const tvtReis = spaarpot('TVT_REIS')
  const atv = spaarpot('ATV')

  return {
    voldoendeInDienst: input.maandenInDienst >= ADVIES_MIN_MAANDEN_IN_DIENST,
    maandenInDienst: input.maandenInDienst,
    totaalOpgenomen,
    gewoon: potGraad(gewoonBeschikbaar, gewoonGebruikt, j.WETTELIJK.eind + j.BOVENWETTELIJK.eind),
    atv,
    tvtWerk,
    tvtReis,
    tekort: round2(rest),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Advies
// ────────────────────────────────────────────────────────────────────────────

/**
 * De redenen van het advies: een code plus de echte getallen. De tekst per code
 * staat in `ADVIES_REDEN_TEKSTEN` (@streep/types); `renderAdviesReden` vult hem in.
 */
export type AdviesReden = AdviesRedenDTO

export interface OpbouwAdvies {
  /** Het advies, zoals het in het systeem kan (variant b: zonder ATV geen TVT). */
  advies: OpbouwKeuze
  /** Mag ATV in het advies en in "zelf instellen" worden getoond (38 uur met een 40-urenweek, of nu aan)? */
  atvTonen: boolean
  categorie: AdviesCategorie
  /** Wat wijzigt ten opzichte van nu. */
  wijzigt: { atv: boolean; overuren: boolean; reistijd: boolean }
  redenen: AdviesReden[]
}

const pct = (graad: number | null): number => Math.round((graad ?? 0) * 100)

/** Een keuze kan alleen omlaag (UIT < HALF < ALLES); nooit meer sparen dan nu. */
function minder(huidig: SpaarKeuze, voorstel: SpaarKeuze): SpaarKeuze {
  const rang: Record<SpaarKeuze, number> = { UITBETALEN: 0, HALF: 1, ALLES: 2 }
  return rang[voorstel] < rang[huidig] ? voorstel : huidig
}

/**
 * De adviesregels van §C11, in deze volgorde:
 *   - te kort in dienst (< 6 maanden): geen advies, keuzes houden;
 *   - gewoon verlof < drempel (0,75): ATV uit en TVT werk/reis uit;
 *   - anders: ATV < drempel (0,50) -> ATV uit; per TVT-soort < 0,25 -> uit, < 0,50 -> half;
 *   - niets te veranderen: keuzes houden (de doorrekening bevestigt het).
 * ATV is alleen adviseerbaar als ATV iets oplevert (38 uur met een 40-urenweek); anders
 * blijft de ATV-keuze staan en wordt hij niet getoond. In variant (b) zet "ATV uit" ook
 * de TVT op uitbetalen (geen ATV = geen TVT), met een eigen reden.
 */
export function bepaalOpbouwAdvies(input: {
  graden: Opnamegraden
  huidig: OpbouwKeuze
  /** ATV levert iets op bij dit contract (`atvMogelijk` uit werktijd-sparen-uitleg). */
  atvMogelijk: boolean
  drempels?: AdviesDrempels
  atvUitVariant?: AtvUitVariant
  /** Jaar van het overzicht en het nieuwe gewone verlof (voor de zinnen). */
  jaar: number
  nieuwGewoonVerlof: number
}): OpbouwAdvies {
  const { graden: g, huidig, atvMogelijk } = input
  const d = input.drempels ?? STANDAARD_ADVIES_DREMPELS
  const variant = input.atvUitVariant ?? STANDAARD_ATV_UIT_VARIANT
  // ATV is een keuze als hij iets oplevert, of als hij nu aan staat en uit kan.
  const atvTonen = atvMogelijk || huidig.atv
  const redenen: AdviesReden[] = []
  const gewenst: OpbouwKeuze = { ...huidig }

  if (!g.voldoendeInDienst) {
    redenen.push({ code: 'TE_KORT_IN_DIENST', waarden: { maanden: g.maandenInDienst, minimum: ADVIES_MIN_MAANDEN_IN_DIENST } })
    return { advies: { ...huidig }, atvTonen, categorie: 'TE_KORT_IN_DIENST', wijzigt: { atv: false, overuren: false, reistijd: false }, redenen }
  }

  let categorie: AdviesCategorie = 'HOUDEN'
  if (g.gewoon.graad != null && g.gewoon.graad < d.gewoon) {
    categorie = 'ALLES_UIT'
    if (atvMogelijk) gewenst.atv = false
    gewenst.overuren = 'UITBETALEN'
    gewenst.reistijd = 'UITBETALEN'
    redenen.push({
      code: 'GEWOON_NIET_OP',
      waarden: {
        opgenomen: g.totaalOpgenomen,
        beschikbaar: g.gewoon.beschikbaar,
        pct: pct(g.gewoon.graad),
        overEind: g.gewoon.overEind,
        nieuw: input.nieuwGewoonVerlof,
        jaar: input.jaar,
        volgendJaar: input.jaar + 1,
      },
    })
  } else {
    const atvWeinigGebruikt = atvMogelijk && huidig.atv && g.atv.graad != null && g.atv.graad < d.atv
    const tvt = (pot: PotGraad, h: SpaarKeuze, uit: AdviesRedenCode, half: AdviesRedenCode): SpaarKeuze => {
      if (h === 'UITBETALEN' || pot.graad == null) return h
      const voorstel: SpaarKeuze = pot.graad < d.tvtUit ? 'UITBETALEN' : pot.graad < d.tvtHalf ? 'HALF' : h
      const nieuw = minder(h, voorstel)
      if (nieuw !== h) {
        redenen.push({
          code: nieuw === 'UITBETALEN' ? uit : half,
          waarden: { gebruikt: pot.gebruikt, beschikbaar: pot.beschikbaar, pct: pct(pot.graad), jaar: input.jaar },
        })
      }
      return nieuw
    }
    gewenst.overuren = tvt(g.tvtWerk, huidig.overuren, 'TVT_WERK_UIT', 'TVT_WERK_HALF')
    gewenst.reistijd = tvt(g.tvtReis, huidig.reistijd, 'TVT_REIS_UIT', 'TVT_REIS_HALF')
    if (atvWeinigGebruikt) {
      // Variant (b): ATV uit betekent ook TVT uit (`tvtFactor`). Blijft de medewerker volgens het
      // advies sparen (hij gebruikt zijn gespaarde uren), dan is ATV nodig om te kunnen sparen:
      // dan adviseren we ATV aan te houden, met die reden. Anders ATV uit.
      // Alleen een potje met gebruik telt (zonder opbouw is er geen "je gebruikt je gespaarde uren").
      const blijftSparen =
        (gewenst.overuren !== 'UITBETALEN' && g.tvtWerk.graad != null) || (gewenst.reistijd !== 'UITBETALEN' && g.tvtReis.graad != null)
      if (variant === 'B_38_UUR' && blijftSparen) {
        redenen.push({ code: 'ATV_NODIG_VOOR_TVT', waarden: { overEind: g.atv.overEind, jaar: input.jaar } })
      } else {
        gewenst.atv = false
        redenen.push({
          code: 'ATV_NIET_GEBRUIKT',
          waarden: { gebruikt: g.atv.gebruikt, beschikbaar: g.atv.beschikbaar, pct: pct(g.atv.graad), overEind: g.atv.overEind, jaar: input.jaar },
        })
      }
    }
    if (gewenst.atv !== huidig.atv || gewenst.overuren !== huidig.overuren || gewenst.reistijd !== huidig.reistijd) {
      categorie = 'MINDER_SPAREN'
    }
  }

  // Variant (b): zonder ATV is er geen TVT (`tvtFactor`). Het advies is dan feitelijk uitbetalen.
  const advies: OpbouwKeuze = { ...gewenst }
  if (variant === 'B_38_UUR' && !advies.atv && (advies.overuren !== 'UITBETALEN' || advies.reistijd !== 'UITBETALEN')) {
    redenen.push({ code: 'ZONDER_ATV_GEEN_TVT', waarden: {} })
    advies.overuren = 'UITBETALEN'
    advies.reistijd = 'UITBETALEN'
  }
  if (!atvMogelijk && huidig.atv) {
    // Staat ATV aan zonder dat hij iets oplevert (parttime, of een gepinde werkweek): blijft staan.
    redenen.push({ code: 'ATV_NIET_ADVISEERBAAR', waarden: {} })
  }
  const wijzigt = {
    atv: advies.atv !== huidig.atv,
    overuren: advies.overuren !== huidig.overuren,
    reistijd: advies.reistijd !== huidig.reistijd,
  }
  if (!wijzigt.atv && !wijzigt.overuren && !wijzigt.reistijd) {
    categorie = 'HOUDEN'
    redenen.push({
      code: 'HOUDEN',
      waarden: { opgenomen: g.totaalOpgenomen, beschikbaar: g.gewoon.beschikbaar, pct: pct(g.gewoon.graad), jaar: input.jaar },
    })
  }
  return { advies, atvTonen, categorie, wijzigt, redenen }
}

/** Een vaste sleutel voor het advies: verandert die, dan is het advies "van categorie veranderd" (§B8). */
export function adviesSleutel(k: OpbouwKeuze): string {
  return `ATV:${k.atv ? 'AAN' : 'UIT'}|OVERUREN:${k.overuren}|REISTIJD:${k.reistijd}`
}

// ────────────────────────────────────────────────────────────────────────────
// 3. Doorrekening 2027
// ────────────────────────────────────────────────────────────────────────────

export interface DoorrekeningInput {
  /** Het eind van het jaaroverzicht per potje en wat er op de jaargrens vervalt. */
  eindJaar: Record<KlassiekeEmmerType, { eind: number; vervaltBijJaareinde: number | null }>
  /** De keuzes van het scenario voor het nieuwe jaar. */
  keuzes: OpbouwKeuze
  /** Contract per 1 januari van het nieuwe jaar. */
  contractUren: number
  /** Levert ATV iets op bij dit contract (38 uur met een 40-urenweek)? */
  atvMogelijk: boolean
  beleid: { wettelijkFactor: number; bovenwettelijkFactor: number; atvUrenPerJaar: number; tvtMaxOverdrachtUren: number }
  /** Verwachte overuren en reisuren in het nieuwe jaar (de realisatie van dit jaar, met "±"). */
  verwacht: { overuren: number; reisuren: number }
  /**
   * De verwachte opname: het patroon van dit jaar (geschaald naar de contracturen) en het
   * al geplande verlof in het nieuwe jaar (de wensen in deze campagne plus andere aanvragen).
   */
  opname: { patroon: number; gepland: number }
  atvUitVariant?: AtvUitVariant
}

/**
 * De uitkomst per scenario (de vorm staat in @streep/types, zodat de portal hem live kan
 * herberekenen met dezelfde functie). Uitbetaald = uren (geld blijft buiten de portal);
 * `overuurGeldFactor` is de factor op elk overuur in het loon (1,5 - TVT-factor).
 */
export type Doorrekening2027 = Doorrekening2027DTO

/**
 * Doorrekening van het nieuwe jaar voor een scenario (§C12). Alles in uren; de
 * payroll-factoren komen uit `payroll-calculations.ts` (`tvtFactor`,
 * `overuurGeldFactor`, `reisGeldFactor`), niet gedupliceerd.
 */
export function berekenDoorrekening2027(input: DoorrekeningInput): Doorrekening2027 {
  const variant = input.atvUitVariant ?? STANDAARD_ATV_UIT_VARIANT
  const k = input.keuzes
  const start = {} as Record<KlassiekeEmmerType, number>
  for (const t of ['WETTELIJK', 'BOVENWETTELIJK', 'ATV', 'TVT_WERK', 'TVT_REIS'] as const) {
    const e = input.eindJaar[t]
    let verval = e.vervaltBijJaareinde ?? 0
    if ((t === 'TVT_WERK' || t === 'TVT_REIS') && e.vervaltBijJaareinde == null) {
      verval = Math.max(0, e.eind - input.beleid.tvtMaxOverdrachtUren)
    }
    start[t] = round2(e.eind - Math.max(0, verval))
  }
  const startTotaal = round2(Object.values(start).reduce((s, v) => s + v, 0))

  const wettelijk = round2(Math.max(0, input.contractUren) * input.beleid.wettelijkFactor)
  const bovenwettelijk = round2(Math.max(0, input.contractUren) * input.beleid.bovenwettelijkFactor)
  const atvOpbouw = k.atv && input.atvMogelijk ? round2(input.beleid.atvUrenPerJaar) : 0
  // In variant (a) blijft de werkweek 40 uur zonder ATV-opbouw; TVT blijft dan mogelijk.
  const atvVoorTvt = variant === 'A_40_UUR_UITBETAALD' ? true : k.atv
  const werkFactor = tvtFactor(k.overuren !== 'UITBETALEN', atvVoorTvt, k.overuren === 'HALF' ? 50 : 100)
  const reisFactor = tvtFactor(k.reistijd !== 'UITBETALEN', atvVoorTvt, k.reistijd === 'HALF' ? 50 : 100)
  const tvtWerk = round2(Math.max(0, input.verwacht.overuren) * werkFactor)
  const tvtReis = round2(Math.max(0, input.verwacht.reisuren) * reisFactor)

  const atvUitbetaald =
    variant === 'A_40_UUR_UITBETAALD' && !k.atv && input.atvMogelijk ? round2(input.beleid.atvUrenPerJaar) : 0
  const overurenUitbetaald = round2(Math.max(0, input.verwacht.overuren) * (1 - werkFactor))
  const reisUitbetaald = round2(Math.max(0, input.verwacht.reisuren) * reisGeldFactor(reisFactor))

  const verwachteOpname = round2(Math.max(input.opname.patroon, input.opname.gepland, 0))
  const erbijTotaal = round2(wettelijk + bovenwettelijk + atvOpbouw + tvtWerk + tvtReis)
  return {
    start,
    startTotaal,
    erbij: {
      wettelijk,
      bovenwettelijk,
      atv: atvOpbouw,
      tvtWerk,
      tvtReis,
      totaal: erbijTotaal,
      gewoon: round2(wettelijk + bovenwettelijk),
    },
    uitbetaald: {
      atv: atvUitbetaald,
      overuren: overurenUitbetaald,
      reisuren: reisUitbetaald,
      totaal: round2(atvUitbetaald + overurenUitbetaald + reisUitbetaald),
      overuurGeldFactor: overuurGeldFactor(werkFactor),
      reisGeldFactor: reisGeldFactor(reisFactor),
    },
    verwachteOpname,
    eind: round2(startTotaal + erbijTotaal - verwachteOpname),
    atvPerMaand: round2((atvOpbouw > 0 ? atvOpbouw : atvUitbetaald) / 12),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 4. De kaarten van stap 2 (PO-herziening 02-11-2026): per keuze twee regels
// ────────────────────────────────────────────────────────────────────────────

const SPAAR_RANG: Record<SpaarKeuze, number> = { UITBETALEN: 0, HALF: 1, ALLES: 2 }

/**
 * Mag de TVT-keuze al per 1 december ingaan (A9)? Alleen als hij omlaag gaat: minder
 * sparen kan meteen, meer sparen niet (dat vraagt ATV, en die gaat per 1 januari).
 */
export function tvtIngangKiesbaar(huidig: OpbouwKeuze, gekozen: OpbouwKeuze): boolean {
  const hO = SPAAR_RANG[huidig.overuren]
  const hR = SPAAR_RANG[huidig.reistijd]
  const gO = SPAAR_RANG[gekozen.overuren]
  const gR = SPAAR_RANG[gekozen.reistijd]
  return gO <= hO && gR <= hR && (gO < hO || gR < hR)
}

const spaarFactor = (s: SpaarKeuze): number => tvtFactor(s !== 'UITBETALEN', true, s === 'HALF' ? 50 : 100)

/** Wat er in december dit jaar al extra wordt uitbetaald bij een TVT-ingang per 1 december. */
export function decemberUitbetaald(
  verwacht: { overuren: number; reisuren: number },
  huidig: OpbouwKeuze,
  gekozen: OpbouwKeuze,
  ingang: CampagneTvtIngang,
): number {
  if (ingang !== 'DECEMBER' || !tvtIngangKiesbaar(huidig, gekozen)) return 0
  const perMaand = (uren: number) => Math.max(0, uren) / 12
  return round2(
    perMaand(verwacht.overuren) * (spaarFactor(huidig.overuren) - spaarFactor(gekozen.overuren)) +
      perMaand(verwacht.reisuren) * (spaarFactor(huidig.reistijd) - spaarFactor(gekozen.reistijd)),
  )
}

/** Eén getal per regel: uren verlof die er in het nieuwe jaar bijkomen, en wat er wordt uitbetaald. */
export interface KeuzeUitkomst {
  verlof: number
  uitbetaald: number
}

export interface KeuzeKaarten {
  /** Gewoon verlof + opgespaarde overuren en reisuren; bij "uit" alleen het gewone verlof. */
  tvt: { aan: KeuzeUitkomst; uit: KeuzeUitkomst; van: (k: OpbouwKeuze) => KeuzeUitkomst }
  /** Alleen de ATV-uren ("extra verlof"). Uitbetaald alleen in variant (a), zie A2. */
  atv: { aan: KeuzeUitkomst & { perMaand: number }; uit: KeuzeUitkomst }
  /** Alles samen voor een scenario, inclusief wat er in december al wordt uitbetaald. */
  samen: (k: OpbouwKeuze) => KeuzeUitkomst & { december: number }
  /** "Mee naar 2027" (stap 1): de start van het nieuwe jaar over alle potjes. */
  meeNaarNieuwJaar: number
}

/** De kaarten van stap 2, uit dezelfde rekenkern als de doorrekening (§C12). */
export function berekenKeuzeKaarten(
  invoer: Omit<DoorrekeningInput, 'keuzes'> & { huidigDecember: OpbouwKeuze; tvtIngang: CampagneTvtIngang },
): KeuzeKaarten {
  const reken = (keuzes: OpbouwKeuze) => berekenDoorrekening2027({ ...invoer, keuzes })
  // De TVT-regels los van de ATV-keuze: "TVT aan" zegt wat sparen oplevert.
  const tvtVan = (k: OpbouwKeuze): KeuzeUitkomst => {
    const d = reken({ ...k, atv: true })
    return { verlof: round2(d.erbij.gewoon + d.erbij.tvtWerk + d.erbij.tvtReis), uitbetaald: round2(d.uitbetaald.overuren + d.uitbetaald.reisuren) }
  }
  const variant = invoer.atvUitVariant ?? STANDAARD_ATV_UIT_VARIANT
  const atvUren = invoer.atvMogelijk ? round2(invoer.beleid.atvUrenPerJaar) : 0
  return {
    tvt: {
      aan: tvtVan({ atv: true, overuren: 'ALLES', reistijd: 'ALLES' }),
      uit: tvtVan({ atv: true, overuren: 'UITBETALEN', reistijd: 'UITBETALEN' }),
      van: tvtVan,
    },
    atv: {
      aan: { verlof: atvUren, uitbetaald: 0, perMaand: round2(atvUren / 12) },
      uit: { verlof: 0, uitbetaald: variant === 'A_40_UUR_UITBETAALD' ? atvUren : 0 },
    },
    samen: (k) => {
      const d = reken(k)
      const december = decemberUitbetaald(invoer.verwacht, invoer.huidigDecember, k, invoer.tvtIngang)
      return { verlof: d.erbij.totaal, uitbetaald: round2(d.uitbetaald.totaal + december), december }
    },
    meeNaarNieuwJaar: reken(invoer.huidigDecember).startTotaal,
  }
}

/**
 * Het verwachte aantal overuren of reisuren in het nieuwe jaar (A8): de realisatie van
 * dit jaar, doorgetrokken naar een heel jaar als er pas een deel van het jaar gemeten
 * is. Altijd met "±" tonen.
 */
export function verwachtePerJaar(gemeten: number, gemetenMaanden: number): number {
  if (!(gemeten > 0) || !(gemetenMaanden > 0)) return 0
  return round2(gemetenMaanden >= 12 ? gemeten : (gemeten / gemetenMaanden) * 12)
}

/** Afronden op een half uur, alleen voor het tonen (§C12: "afronden op 0,5 u pas bij tonen"). */
export function rondAfOpHalfUur(uren: number): number {
  return Math.round(uren * 2) / 2
}
